const signature = (cart) => JSON.stringify((cart?.items || []).map((item) => [item.key, item.quantity]));

const childUrl = (endpoint, path) => {
    const url = new URL(endpoint, window.location.href);
    url.pathname = `${url.pathname.replace(/\/$/, '')}/${path}`;
    return url.toString();
};

export const initCartSharing = (drawer, { endpoint, request, runTask, renderSnapshot, refreshWooFragments, open, getSnapshot }) => {
    const shareButton = drawer.querySelector('[data-cart-share]');
    const output = drawer.querySelector('[data-cart-share-output]');
    const input = drawer.querySelector('[data-cart-share-url]');
    const message = drawer.querySelector('[data-cart-share-message]');
    const nativeShare = drawer.querySelector('[data-cart-share-native]');
    const panel = drawer.querySelector('[data-cart-shared]');
    const sharedStatus = drawer.querySelector('[data-cart-shared-status]');
    const sharedItems = drawer.querySelector('[data-cart-shared-items]');
    const restoreButton = drawer.querySelector('[data-cart-shared-restore]');
    const replace = drawer.querySelector('[data-cart-shared-replace]');
    let generatedSignature = '';
    let busy = false;

    const copyLink = async () => {
        try {
            if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
            await navigator.clipboard.writeText(input.value);
        } catch {
            input.focus({ preventScroll: true });
            input.select();
            if (!document.execCommand('copy')) {
                message.textContent = 'Link jest gotowy. Skopiuj go z pola powyżej i wyślij wybranej osobie.';
                return;
            }
        }
        message.textContent = 'Link skopiowany. Możesz go teraz wysłać. Jest ważny przez 30 dni.';
    };

    shareButton?.addEventListener('click', async () => {
        if (busy) return;
        busy = true;
        shareButton.disabled = true;
        shareButton.textContent = 'Przygotowuję link…';
        try {
            const payload = await runTask(() => request(childUrl(endpoint, 'share'), { method: 'POST', body: {} }));
            const url = new URL(payload.share.url, window.location.href);
            if (url.origin !== window.location.origin || !['http:', 'https:'].includes(url.protocol)) throw new Error('Nieprawidłowy link do koszyka.');
            generatedSignature = signature(getSnapshot());
            input.value = url.href;
            output.hidden = false;
            drawer.querySelector('[data-cart-scroll]').scrollTop = 0;
            nativeShare.hidden = typeof navigator.share !== 'function';
            await copyLink();
        } catch (error) {
            output.hidden = false;
            message.textContent = error.message || 'Nie udało się udostępnić koszyka. Spróbuj ponownie.';
        } finally {
            busy = false;
            shareButton.disabled = false;
            shareButton.textContent = 'Udostępnij koszyk';
        }
    });
    drawer.querySelector('[data-cart-share-copy]')?.addEventListener('click', copyLink);
    input?.addEventListener('click', () => input.select());
    nativeShare?.addEventListener('click', async () => {
        try {
            await navigator.share({ title: 'Mój koszyk Seluvia', url: input.value });
        } catch (error) {
            if (error.name !== 'AbortError') message.textContent = 'Skopiuj link i wyślij go w wybranej aplikacji.';
        }
    });
    document.addEventListener('seluvia:cart', (event) => {
        if (event.detail?.type === 'updated' && generatedSignature && generatedSignature !== signature(event.detail.payload)) {
            output.hidden = true;
            generatedSignature = '';
            input.value = '';
        }
    });

    const hash = new URLSearchParams(window.location.hash.slice(1));
    const token = hash.get('shared-cart');
    if (!token) return;

    panel.hidden = false;
    drawer.classList.add('has-shared-cart');
    window.requestAnimationFrame(() => open({ refreshCart: true }));
    sharedStatus.textContent = 'Wczytuję udostępniony koszyk…';

    const clearSharedLink = () => {
        const url = new URL(window.location.href);
        url.hash = '';
        url.searchParams.delete('seluvia-cart');
        window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}`);
    };
    drawer.querySelector('[data-cart-shared-dismiss]')?.addEventListener('click', () => {
        panel.hidden = true;
        drawer.classList.remove('has-shared-cart');
        clearSharedLink();
    });

    const preview = async () => {
        try {
            if (!/^[a-f0-9]{48}$/.test(token)) throw new Error('Ten link do koszyka jest nieprawidłowy. Poproś o nowy link.');
            const payload = await request(childUrl(endpoint, `shared/${token}`));
            const rows = payload.shared_cart.items;
            sharedItems.replaceChildren(...rows.map((item) => {
                const row = document.createElement('li');
                const name = document.createElement(item.url ? 'a' : 'span');
                if (item.url) {
                    const url = new URL(item.url, window.location.origin);
                    if (url.origin === window.location.origin && ['http:', 'https:'].includes(url.protocol)) name.href = url.href;
                }
                name.textContent = `${item.quantity} × ${item.name}`;
                row.append(name);
                const detail = document.createElement('small');
                detail.textContent = [item.variation, item.message].filter(Boolean).join(' · ');
                row.append(detail);
                if (!item.available) row.classList.add('is-unavailable');
                return row;
            }));
            const available = rows.some((item) => item.available);
            restoreButton.hidden = !available;
            replace.closest('label').hidden = !available;
            sharedStatus.textContent = available
                ? 'Odtwórz produkty, warianty i ilości. Obowiązują aktualne ceny i promocje. Niedostępne pozycje zostaną pominięte.'
                : 'Produkty z tego linku są obecnie niedostępne. Twój koszyk pozostał bez zmian.';
        } catch (error) {
            sharedStatus.textContent = error.message || 'Nie udało się wczytać udostępnionego koszyka.';
        }
    };
    restoreButton?.addEventListener('click', async () => {
        restoreButton.disabled = true;
        replace.disabled = true;
        try {
            const payload = await runTask(() => request(childUrl(endpoint, `shared/${token}`), {
                method: 'POST', body: { mode: replace.checked ? 'replace' : 'merge' },
            }));
            renderSnapshot(payload, { announceUpdate: true });
            refreshWooFragments();
            restoreButton.hidden = true;
            replace.closest('label').hidden = true;
            sharedItems.hidden = true;
            sharedStatus.textContent = ['Koszyk odtworzony.', ...(payload.shared_cart_result?.warnings || [])].join(' ');
            clearSharedLink();
            sharedStatus.focus();
        } catch (error) {
            sharedStatus.textContent = error.message || 'Nie udało się odtworzyć koszyka. Spróbuj ponownie.';
        } finally {
            restoreButton.disabled = false;
            replace.disabled = false;
        }
    });
    preview();
};
